import React from "react";
import { Grid2 as Grid, MenuItem, Paper, TextField, Typography, Button } from "@mui/material";
import { pascalEnumToString } from "../../util/jbhTextFormatterUtil";
import { LargeActionButton } from "../Common/LargeActionButton";
import PageContext from "../../components/Common/PageContext";
import { useStyles } from "./CreditRequestLineItemRow.styles";
import { CreditRequestLineItemRowProps } from "./types";
import PencilIcon from "@jbhi-fi/lanyard-ui/components/Icons/Pencil";

export default function CreditRequestLineItemRow({
	creditRequestLineItem,
	creditRequestReasons,
	updateLineItem,
	copyLineItemDetailsToAllLines = () => {},
	showCopyToAll,
	forceCancelReason,
	hideCourier,
}: CreditRequestLineItemRowProps) {
	const classes = useStyles();
	const hasCancelled = forceCancelReason || creditRequestLineItem.quantity === 0;
	const reasons = creditRequestReasons
		.filter((reason) => reason.type === (hasCancelled ? "Cancel" : "Create"))
		.map((reason) => reason.value);

	const [pageContext] = React.useContext(PageContext);
	const { carriers: couriers } = pageContext;

	const [isEditingConsignment, setIsEditingConsignment] = React.useState(false);
	const showConsignmentReadOnly = Boolean(creditRequestLineItem.receivedConsignmentNumber) && !isEditingConsignment;
	const lineItemKey = creditRequestLineItem.id ?? creditRequestLineItem.sku;

	const handleConsignmentBlur = () => {
		if (creditRequestLineItem.receivedConsignmentNumber) {
			setIsEditingConsignment(false);
		}
	};

	const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		hasCancelled
			? updateLineItem({ ...creditRequestLineItem, cancelReason: value })
			: updateLineItem({ ...creditRequestLineItem, reason: value });
	};

	const handleConsignmentNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		updateLineItem({ ...creditRequestLineItem, receivedConsignmentNumber: event.target.value });
	};

	const handleCarrierNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		updateLineItem({ ...creditRequestLineItem, receivedCarrierName: event.target.value });
	};
	const copyLineItemDetailsAcrossAll = () => {
		copyLineItemDetailsToAllLines(creditRequestLineItem);
	};

	return (
		<Grid size={12}>
			<Paper elevation={0} className={classes.paper}>
				<Typography variant="body1" className={classes.description}>
					{creditRequestLineItem.description}
				</Typography>
				<Grid container className={classes.labelRow}>
					<Grid size={8}>
						<Typography variant="body2">
							SKU: <span className={classes.value}>{creditRequestLineItem.sku}</span>
						</Typography>
					</Grid>
					<Grid size={4}>
						<Typography variant="body2" align="right" pr={0.5}>
							Unit(s): <span>{creditRequestLineItem.quantity}</span>
						</Typography>
					</Grid>
					<Grid size={12}>
						<Typography variant="body2">
							Model: <span className={classes.value}>{creditRequestLineItem.modelNumber}</span>
						</Typography>
					</Grid>
				</Grid>
				{!forceCancelReason &&
					(showConsignmentReadOnly ? (
						<div className={classes.consignmentRow}>
							<Typography variant="body2">
								Consignment No:{" "}
								<span style={{ textDecoration: "underline" }}>{creditRequestLineItem.receivedConsignmentNumber}</span>
							</Typography>
							<Button
								startIcon={<PencilIcon width={16} />}
								className={classes.editButton}
								onClick={() => setIsEditingConsignment(true)}
							>
								Edit
							</Button>
						</div>
					) : (
						<Grid container className={classes.reasonRow}>
							<Grid size={12}>
								<TextField
									id={`consignmentnumber-${lineItemKey}`}
									label="Consignment No."
									variant="outlined"
									size="small"
									fullWidth
									error={!creditRequestLineItem.receivedConsignmentNumber}
									value={creditRequestLineItem.receivedConsignmentNumber ?? ""}
									onChange={handleConsignmentNumberChange}
									onFocus={() => setIsEditingConsignment(true)}
									onBlur={handleConsignmentBlur}
									className={classes.field}
									slotProps={{
										inputLabel: { shrink: Boolean(creditRequestLineItem.receivedConsignmentNumber) },
										htmlInput: { autoCorrect: "off" },
									}}
									autoComplete="off"
								/>
							</Grid>
						</Grid>
					))}
				<Grid container spacing={1} alignItems="stretch" className={classes.reasonRow}>
					<Grid size={showCopyToAll ? 8 : 12}>
						<TextField
							id={`reason-${lineItemKey}`}
							select
							label="Reason"
							variant="outlined"
							size="small"
							fullWidth
							value={(hasCancelled ? creditRequestLineItem.cancelReason : creditRequestLineItem.reason) ?? ""}
							onChange={handleReasonChange}
							className={classes.field}
							slotProps={{
								inputLabel: {
									shrink: Boolean(hasCancelled ? creditRequestLineItem.cancelReason : creditRequestLineItem.reason),
								},
							}}
						>
							{reasons.map((reason) => (
								<MenuItem key={reason} value={reason}>
									{pascalEnumToString(reason)}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					{showCopyToAll && (
						<Grid size={4}>
							<LargeActionButton
								variant="outlined"
								onClick={copyLineItemDetailsAcrossAll}
								disabled={false}
								className={classes.copyButton}
								children="Copy to all"
							/>
						</Grid>
					)}
				</Grid>
				{!forceCancelReason && !hideCourier && (
					<Grid container className={classes.reasonRow}>
						<Grid size={12}>
							<TextField
								id={`couriers-${lineItemKey}`}
								select
								label="Courier"
								variant="outlined"
								size="small"
								fullWidth
								value={creditRequestLineItem.receivedCarrierName ?? ""}
								onChange={handleCarrierNameChange}
								className={classes.field}
								slotProps={{
									inputLabel: { shrink: Boolean(creditRequestLineItem.receivedCarrierName) },
								}}
							>
								{couriers.map((courier) => (
									<MenuItem key={courier} value={courier}>
										{courier}
									</MenuItem>
								))}
							</TextField>
						</Grid>
					</Grid>
				)}
			</Paper>
		</Grid>
	);
}
